import React, { useState, useRef } from 'react';
import { TextField, Typography, InputAdornment } from '@mui/material';
import StyledTextField from '../styled-components/StyledTextField';
import { MAX_LENGTH_FIELDS } from '../../constants/field-constants';
import {
  LATIN_TEXT_PATTERN,
  DUPLICATE_SYMBOLS_PATTERN,
  OUTER_EDGES_PATTERN,
  HAS_ILLEGAL_SYMBOLS_PATTERN
} from '../../constants/patterns-constants';
import AlertService from '../../services/notification.service';

type TextFieldProps = React.ComponentProps<typeof TextField>;

interface LatinTextFieldProps extends TextFieldProps {
  error?: boolean;
  errorFields?: boolean;
  setErrorFields?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  name: string;
  required?: boolean;
  check?: boolean;
  value?: string;
}

export const LatinTextField = ({
                                 value,
                                 onChange,
                                 label,
                                 name,
                                 disabled,
                                 required,
                                 check,
                                 error,
                                 errorFields,
                                 setErrorFields,
                                 ...rest
                               }:LatinTextFieldProps) => {

  const wasAlertedRef = useRef(false);
  const wasFocusedRef = useRef(false);
  const wasTrimmedRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);
  const str = String(value ?? '');
  const cleanOnChange = (raw: string): string => {
    return raw.replace(LATIN_TEXT_PATTERN, '').replace(DUPLICATE_SYMBOLS_PATTERN, '$1');
  };
  const trimOuterEdges= (raw: string): string => {
    return raw.replace(OUTER_EDGES_PATTERN, '');
  }
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let rawValue: string = e.target.value;

    let trimmed: boolean = false;
    if (rawValue.length > MAX_LENGTH_FIELDS) {
      rawValue = rawValue.slice(0, MAX_LENGTH_FIELDS);
      trimmed = true;
    }

    const hasIllegalChars = HAS_ILLEGAL_SYMBOLS_PATTERN.test(rawValue);
    const cleaned = cleanOnChange(rawValue);
    const syntheticEvent = { target: { value: cleaned } } as React.ChangeEvent<HTMLInputElement>;

    if (trimmed && !wasTrimmedRef.current) {
      AlertService.warning(`The text entered in the ${label} field was truncated to ${MAX_LENGTH_FIELDS} characters`);
      wasTrimmedRef.current = true;
      setErrorFields?.(prev => ({...prev, [name]: true}));
    } else {
      setErrorFields?.(prev => ({...prev, [name]: false}));
    }

    if (hasIllegalChars && !wasAlertedRef.current) {
      AlertService.warning(`Invalid characters have been removed from the ${label} field. Only Latin letters, space, hyphen and apostrophe are allowed`);
      wasAlertedRef.current = true;
      setErrorFields?.(prev => ({...prev, [name]: true}));
    } else {
      setErrorFields?.(prev => ({...prev, [name]: false}));
    }

    onChange?.(syntheticEvent);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const cleaned = trimOuterEdges(str);
    if (cleaned !== str) {
      const syntheticEvent = { target: { value: cleaned } } as React.ChangeEvent<HTMLInputElement>;
      AlertService.warning(`Leading and trailing spaces, apostrophes, and hyphens in the ${label} field have been removed.`);
      setErrorFields?.(prev => ({...prev, [name]: true}));
      onChange?.(syntheticEvent);
    }
    const hasRemainingIssues = HAS_ILLEGAL_SYMBOLS_PATTERN.test(cleaned);
    setErrorFields?.(prev => ({...prev, [name]: hasRemainingIssues}));
    if (required && !str && check)  {
      AlertService.warning(`Please fill in the field ${label}.`);
      setErrorFields?.(prev => ({...prev, [name]: true}));
    }
    wasAlertedRef.current = false;
    wasTrimmedRef.current = false;
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (!wasFocusedRef.current) {
      // AlertService.warning(`Please enter only Latin letters in the ${label} field (allowed: hyphen, space, apostrophe).`);
      wasFocusedRef.current = true;
    }
  };
  return (
    <StyledTextField
      {...rest}
      label={label}
      value={value ?? ""}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      required={required || false}
      onChange={handleInputChange}
      error={error || errorFields}
      slotProps={{
        input: {
          endAdornment:
            isFocused ? (
              <InputAdornment position="end">
                <Typography
                  variant="caption"
                  color={errorFields ? 'error' : 'textSecondary'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {str.length}/{MAX_LENGTH_FIELDS}
                </Typography>

              </InputAdornment>
            ) : null
        }
      }}
    />
  );
};